'use client';

import { useState, useCallback, useMemo } from 'react';
import type { RowData } from '@tanstack/react-table';
import type { Table } from '@/lib/table-features';

interface UseSelectAllPagesReturn {
  isAllPagesSelected: boolean;
  setAllPagesSelected: (value: boolean) => void;
  isCurrentPageFullySelected: boolean;
  totalCount: number;
  reset: () => void;
}

export function useSelectAllPages<TData extends RowData>(
  table: Table<TData>,
  totalCount: number
): UseSelectAllPagesReturn {
  // A unique object reference that changes whenever page/sort/filter context changes.
  // When the context changes, selectedContext !== contextVersion → isAllPagesSelected = false.
  const pageSize = table.state.pagination.pageSize;
  const pageIndex = table.state.pagination.pageIndex;
  const sorting = table.state.sorting;
  const columnFilters = table.state.columnFilters;
  const isCurrentPageFullySelected = table.getIsAllPageRowsSelected();

  const contextVersion = useMemo(
    () =>
      `${pageIndex}|${pageSize}|${JSON.stringify(sorting)}|${JSON.stringify(columnFilters)}`,
    [pageIndex, pageSize, sorting, columnFilters]
  );

  const [selectedContext, setSelectedContext] = useState<string | null>(null);

  // Derived: all-pages is selected only when context matches AND current page is fully selected
  const isAllPagesSelected =
    selectedContext === contextVersion && isCurrentPageFullySelected;

  const setAllPagesSelected = useCallback(
    (value: boolean) => {
      setSelectedContext(value ? contextVersion : null);
    },
    [contextVersion]
  );

  const reset = useCallback(() => {
    setSelectedContext(null);
    table.resetRowSelection();
  }, [table]);

  return useMemo(
    () => ({
      isAllPagesSelected,
      setAllPagesSelected,
      isCurrentPageFullySelected,
      totalCount,
      reset
    }),
    [
      isAllPagesSelected,
      setAllPagesSelected,
      isCurrentPageFullySelected,
      totalCount,
      reset
    ]
  );
}
